import { SessionManager } from '@chargify/session-manager';

import { requestHandler, prepareQueryParams } from './utils';

import {
  APIResponse,
  Subscription,
  SubscriptionComponent,
  HttpMethod,
  ProductFamily,
  CustomerDetails,
  Division,
  AllocationsPreview,
  Allocation,
  InvoicesParams,
  InvoicesList,
  CustomField,
  GatewayConfig,
  SubscriptionPaymentProfile,
  SubscriptionProduct as Product,
  BillingPortalSettings,
} from './types';
import { CustomFieldUpdate } from './types/customField';
import { ProductNames } from './types/invoicesList';
import { AllocationResponse } from './types/componentAllocation';
import {
  ChurnReason,
  SubscriptionResumptionCharge,
  SubscriptionWithCoupon,
} from './types/subscription';
import { PaymentProfile } from './types/paymentProfiles';
import snakecaseKeys from 'snakecase-keys';

export interface DataAccessOptions {
  /**
   * Components API base url
   */
  baseUrl?: string;
  /**
   * Session manager instance
   */
  sessionManager: SessionManager;
}

class DataAccess {
  /** @type {string} */
  public baseUrl: string = null;

  /** @type {SessionManager} */
  public sessionManager: SessionManager = null;

  constructor({ baseUrl, sessionManager }: DataAccessOptions) {
    this.sessionManager = sessionManager;
    this.baseUrl = baseUrl;
  }

  /**
   * Performs request to API
   * @param {string} url - resource url
   * @param {Record} body - request body payload
   * @param {HttpMethod} method - http request method
   * @returns {Promise}
   */
  performRequest<R>(
    url: string,
    body: Record<string, any> = null,
    method: HttpMethod = HttpMethod.GET
  ) {
    return requestHandler<R>(url, body, method, {
      Authorization: `Bearer ${this.sessionManager.accessToken}`,
    });
  }

  /**
   * Fetches product catalogs related with customer subscriptions
   * @returns {Promise} Collection of product catalogs
   */
  async getProductsCatalog() {
    return this.performRequest<APIResponse<{ items: ProductFamily[] }>>(
      `${this.baseUrl}/subscriptions/product_families`
    );
  }

  /**
   * Fetches billing portal settings
   * @returns {Promise} Billing portal settings
   */
  async getBillingPortalSettings() {
    return this.performRequest<APIResponse<BillingPortalSettings>>(
      `${this.baseUrl}/settings`
    );
  }
  /**
   *
   * Fetches products for specific product family
   * @returns {Promise} Collection of products
   */
  async getProducts(productFamilyId: number) {
    return this.performRequest<APIResponse<{ items: Product[] }>>(
      `${this.baseUrl}/product_families/${productFamilyId}/products`
    );
  }
  /**
   * Preview subscription product migration
   * @returns {Promise} Product migration preview
   */
  async previewProductMigration(subscriptionId: number, productId: number) {
    return this.performRequest<APIResponse<{ items: Product[] }>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/preview_product_migration`,
      {
        product_id: productId,
      },
      HttpMethod.POST
    );
  }
  /**
   * Migration subscription to another product
   * @returns {Promise} Subscription entity
   */
  async migrateProduct(subscriptionId: number, productId: number) {
    return this.performRequest<APIResponse<Subscription>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/products/${productId}`,
      {},
      HttpMethod.PUT
    );
  }
  /**
   * Fetches subscriptions components
   * @param {number} subscriptionId - subscription identifer
   * @returns {Promise} Subscriptions collection
   */
  async getComponents(subscriptionId: number) {
    return this.performRequest<APIResponse<{ items: SubscriptionComponent[] }>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/components`
    );
  }

  /**
   * Fetches gateway configuration
   * @returns {Promise} Gateway configuration
   */
  async getGatewayConfig() {
    return this.performRequest<APIResponse<GatewayConfig>>(
      `${this.baseUrl}/gateway_config`
    ).then((response) => ({
      ...response,
      vaultCurrencies: snakecaseKeys(response.vaultCurrencies),
    }));
  }
  /**
   * Creates payment profile
   * @param {number} token - tokenized payment method
   * @returns {Promise} Payment profile entity
   */
  async createPaymentProfile(token: string) {
    return this.performRequest<APIResponse<SubscriptionPaymentProfile>>(
      `${this.baseUrl}/payment_profiles`,
      { token },
      HttpMethod.POST
    );
  }

  /**
   * Update payment profile for subscription
   * @param {number} paymentProfileId - payment profile identifier
   * @param {number} subscriptionId - subscriptions identifier
   * @returns {Promise}
   */
  async updateSubscriptionPaymentProfile(
    paymentProfileId: number,
    subscriptionId: number
  ) {
    return this.performRequest<APIResponse<{}>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/payment_profiles/${paymentProfileId}`,
      {},
      HttpMethod.PUT
    );
  }
  /**
   * Replace existing payment profiles with new one
   * @returns {Promise}
   * @param {Number[]} paymentProfileIdsForReplacement
   * @param {Number} newPaymentProfileId
   */
  async replacePaymentProfiles(
    paymentProfileIdsForReplacement: number[],
    newPaymentProfileId: number
  ) {
    return this.performRequest<APIResponse<{}>>(
      `${this.baseUrl}/subscriptions/payment_profiles`,
      {
        billing: {
          payment_profile_id: newPaymentProfileId,
        },
        paymentProfiles: paymentProfileIdsForReplacement.map((id) => ({ id })),
      },
      HttpMethod.PUT
    );
  }

  /**
   * Resume customer subscriptions
   * @returns {Promise} - empty response
   */
  async resumeSubscription(subscriptionId: number) {
    return this.performRequest<APIResponse<{}>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/resume`,
      null,
      HttpMethod.POST
    );
  }
  /**
   * Fetches resumption config for specific subscription
   * @returns {Promise} Resumption charges config
   */
  async getSubscriptionResumptionConfig() {
    return this.performRequest<
      APIResponse<{
        resumption_charge: SubscriptionResumptionCharge;
      }>
    >(`${this.baseUrl}/subscriptions/resumption_config`);
  }
  /**
   * Updates subscription automatically resume date
   * @returns {Promise} Subscription automatically resume date
   */
  async updateSubscriptionResumptionDate(
    subscriptionId: number,
    autoResumeDate: string
  ) {
    return this.performRequest<
      APIResponse<{
        resumption_charge: SubscriptionResumptionCharge;
      }>
    >(
      `${this.baseUrl}/subscriptions/${subscriptionId}/hold`,
      {
        automatically_resume_at: autoResumeDate,
      },
      HttpMethod.PUT
    );
  }
  /**
   * Fetches customer subscriptions
   * @returns {Promise} Subscriptions collection
   */
  async getSubscriptions() {
    return this.performRequest<APIResponse<{ items: Subscription[] }>>(
      `${this.baseUrl}/subscriptions`
    );
  }
  /**
   * Fetches customer subscription by subscription id
   * @returns {Promise} Subscription entity
   */
  async getSubscriptionDetails(subscriptionId: number) {
    return this.performRequest<APIResponse<SubscriptionWithCoupon>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/details`
    );
  }

  /**
   * Adds coupon to subscription
   * @returns {Promise} Coupon entity
   */
  async addCoupon(subscriptionId: number, couponCode: string) {
    return this.performRequest<APIResponse<SubscriptionWithCoupon>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/coupons`,
      { coupon_code: couponCode },
      HttpMethod.PUT
    );
  }

  /**
   * Fetches customer contact details
   * @returns {Promise} Contact details
   */
  async getCustomerDetails() {
    return this.performRequest<APIResponse<CustomerDetails>>(
      `${this.baseUrl}/customer`
    );
  }

  /**
   * Updates customer contact details
   * @returns {Promise} Contact details
   */
  async updateCustomerDetails(customerDetails: CustomerDetails) {
    const { address2, ...rest } = customerDetails;
    const data = { address_2: address2, ...rest };
    return this.performRequest<APIResponse<CustomerDetails>>(
      `${this.baseUrl}/customer`,
      data,
      HttpMethod.PUT
    );
  }

  /**
   * Fetches list of subdivisiona based on country code
   * @param {string} countryCode - country code
   * @returns {Promise} list of subdivisions
   */
  async getCountrySubdivisions(countryCode: string) {
    return this.performRequest<APIResponse<{ items: Division[] }>>(
      `${this.baseUrl}/subdivisions?${prepareQueryParams({ countryCode })}`
    );
  }

  /**
   * Fetches list of countries
   * @returns {Promise} list of countries
   */
  async getCountries() {
    return this.performRequest<APIResponse<{ items: Division[] }>>(
      `${this.baseUrl}/countries`
    );
  }

  /**
   * Fetches list of invoices
   * @params {Object} query - query parameters for pagination and sorting
   * @returns {Promise} list of billingHistory
   */
  async getInvoices(requestParams: InvoicesParams) {
    return this.performRequest<APIResponse<InvoicesList>>(
      `${this.baseUrl}/invoices?${prepareQueryParams(requestParams)}`
    );
  }

  async getInvoiceFilters() {
    return this.performRequest<APIResponse<ProductNames>>(
      `${this.baseUrl}/invoices/filters`
    );
  }

  /**
   * Gets component allocations
   * @returns {Promise} component allocations preview
   */
  async previewComponentAllocations(
    subscriptionId: number,
    body: { allocations: Allocation[] }
  ) {
    return this.performRequest<
      APIResponse<{ allocationsPreview: AllocationsPreview }>
    >(
      `${this.baseUrl}/subscriptions/${subscriptionId}/preview_allocations`,
      body,
      HttpMethod.POST
    );
  }

  /**
   * Updates component allocations
   * @returns {Promise} list of component allocations
   */
  async updateComponentAllocations(
    subscriptionId: number,
    body: { allocations: Allocation[] }
  ) {
    return this.performRequest<APIResponse<AllocationResponse>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/allocations`,
      body,
      HttpMethod.POST
    );
  }

  /**
   * Gets subscription custom fields
   * @returns {Promise} custom fields
   */
  async getSubscriptionCustomFields(subscriptionId: number) {
    return this.performRequest<APIResponse<{ items: CustomField[] }>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/custom_fields`
    );
  }

  /**
   * Gets customer custom fields
   * @returns {Promise} custom fields
   */
  async getCustomerCustomFields() {
    return this.performRequest<APIResponse<{ items: CustomField[] }>>(
      `${this.baseUrl}/customer/custom_fields`
    );
  }

  /**
   * Updates customer custom fields
   * @returns {Promise} Updated customer custom fields
   */
  async updateCustomerCustomFields(customFields: CustomFieldUpdate[]) {
    return this.performRequest<APIResponse<{ fields: CustomFieldUpdate[] }>>(
      `${this.baseUrl}/customer/custom_fields`,
      { fields: customFields },
      HttpMethod.PUT
    );
  }

  /**
   * Updates subscription custom fields
   * @returns {Promise} Updated subscription custom fields
   */
  async updateSubscriptionCustomFields(
    subscriptionId: number,
    customFields: CustomFieldUpdate[]
  ) {
    return this.performRequest<APIResponse<{ fields: CustomFieldUpdate[] }>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/custom_fields`,
      { fields: customFields },
      HttpMethod.PUT
    );
  }

  /**
   * Pauses subscription
   * @returns {Promise} Updated subscription state
   */
  async pauseSubscription(
    subscriptionId: number,
    automaticallyResumeAt: Date | null
  ) {
    return this.performRequest<
      APIResponse<{
        subscriptionId: number;
        automaticallyResumeAt: string | null;
        state: string;
      }>
    >(
      `${this.baseUrl}/subscriptions/${subscriptionId}/hold`,
      { automaticallyResumeAt },
      HttpMethod.POST
    );
  }

  /**
   * Cancels subscription
   * @returns {Promise} Updated subscription state and scheduled cancellation date
   */
  async cancelSubscription({
    subscriptionId,
    ...rest
  }: {
    subscriptionId: number;
    cancellationMessage?: string;
    scheduledCancellationAt?: string | null;
    cancelAtEndOfPeriod?: boolean;
    churnReasonCode?: string;
  }) {
    {
      return this.performRequest<
        APIResponse<{
          canceledAt?: string;
          subscriptionId: number;
          scheduledCancellationAt: string | null;
          state: string;
        }>
      >(
        `${this.baseUrl}/subscriptions/${subscriptionId}/cancel`,
        rest,
        HttpMethod.POST
      );
    }
  }
  /**
   * Get payment profiles
   * @returns {Promise} - list of payment profiles
   */
  async getPaymentProfiles() {
    return this.performRequest<APIResponse<PaymentProfile[]>>(
      `${this.baseUrl}/payment_profiles`
    );
  }
  /**
   * Delete payment profile
   * @returns {Promise} - empty response
   */
  async deletePaymentProfile(paymentProfileId: number) {
    return this.performRequest<APIResponse<PaymentProfile[]>>(
      `${this.baseUrl}/payment_profiles/${paymentProfileId}`,
      {},
      HttpMethod.DELETE
    );
  }

  /**
   * Undos subscription cancelation
   * @returns {Promise} - empty response
   */
  async undoCancelation(subscriptionId: number) {
    return this.performRequest<APIResponse<{}>>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/cancel`,
      null,
      HttpMethod.DELETE
    );
  }

  /**
   * Subscription churn reason codes
   * @returns {Promise} List of churn reason codes
   */
  async getChurnReasonCodes() {
    return this.performRequest<APIResponse<{ items: ChurnReason[] }>>(
      `${this.baseUrl}/churn_reason_codes`
    );
  }

  /**
   * Gets invoice pdf blob
   * @returns {Promise} Invoice pdf blob
   */
  async getInvoicePDF(invoiceId: string) {
    return await fetch(`${this.baseUrl}/invoices/${invoiceId}/pdf`, {
      body: null,
      headers: {
        Authorization: `Bearer ${this.sessionManager.accessToken}`,
        'Content-Type': 'application/pdf',
      },
      method: HttpMethod.GET,
    }).then((resp) => resp.arrayBuffer());
  }
  /**
   * Subscription reactivation
   * @returns {Promise} Updated subscription state
   */
  async reactivateSubscription(subscriptionId: number, couponCode?: string) {
    return this.performRequest<
      APIResponse<{
        id: number;
        currentBillingAmountInCents: number;
        nextAssessmentAt: string;
        currentPeriodEndsAt: string;
      }>
    >(
      `${this.baseUrl}/subscriptions/${subscriptionId}/reactivate`,
      { couponCode },
      HttpMethod.PUT
    );
  }
}

export default DataAccess;
